(() => {
  'use strict';

  const STATE_CLASSES = {
    submitting: 'kns-notify--submitting',
    success: 'kns-notify--success',
    error: 'kns-notify--error',
  };

  const MESSAGES = {
    success: "Done — we'll email you when it's available.",
    invalid: 'Please check that email address.',
    throttled: 'Too many requests — try again in a minute.',
    network: "Couldn't save that — please try again.",
    heartThrottled: 'Too many requests. Try again in a minute.',
    heartNetwork: "Couldn't save that. Please try again.",
    heartNoEmail: "We couldn't find an email on your account. Enter one below.",
    heartLoadFailed: "Couldn't load your wishlist. Enter your email and we'll let you know.",
  };

  // Copy by (stock, state): the server accepts a save for any variant and decides sold-out-ness
  // itself; the theme's own availability only picks the LOOK and the wording. Approved strings.
  const HEART_COPY = {
    in: {
      off: {
        label: 'Add to wishlist',
        lead: "Save it for later. If it sells out, we'll email you when it's back.",
        ariaLabel: 'Add to wishlist',
      },
      on: {
        label: 'On your wishlist',
        lead: "Saved. If it sells out, we'll email you when it's back. Tap the heart to remove it.",
        ariaLabel: 'Remove from wishlist',
      },
    },
    out: {
      off: {
        label: 'Add to wishlist',
        lead: "Add it to your wishlist and we'll email you once it's back in stock.",
        ariaLabel: 'Add to wishlist',
      },
      on: {
        label: 'On your wishlist',
        lead: "We'll email you once it's back in stock. Tap the heart to remove it.",
        ariaLabel: 'Remove from wishlist',
      },
    },
  };

  // 'hidden' is the silent hide: an in-stock knife has no email form to fall back to, so a failed
  // load (or a 401/409 tap) there hides the whole widget rather than offering anything.
  const HEART_VIEWS = ['loading', 'off', 'on', 'form', 'hidden'];

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // One monotonic request counter per widget root. Every heart GET and POST mints a sequence
  // before it starts, and its answer — success OR failure — is applied only if it is still the
  // newest request at that moment. A variant change goes through loadHeartFor, which mints a new
  // sequence and thereby retires anything still in flight (no answer may outlive its variant).
  const heartSeq = new WeakMap();

  function nextSeq(root) {
    const seq = (heartSeq.get(root) ?? 0) + 1;
    heartSeq.set(root, seq);
    return seq;
  }

  function isStale(root, seq) {
    return heartSeq.get(root) !== seq;
  }

  function showMessage(root, text, isError) {
    const message = root.querySelector('[data-kns-notify-message]');
    message.textContent = text;
    message.hidden = false;
    root.classList.toggle(STATE_CLASSES.error, Boolean(isError));
  }

  function selectedVariant(root) {
    const form = root.parentElement?.querySelector('product-form form');
    return Number.parseInt(form?.querySelector('[name="id"]')?.value, 10);
  }

  function variantAvailable(root) {
    const form = root.parentElement?.querySelector('product-form form');
    return !form?.querySelector('[name="add"]')?.disabled;
  }

  function syncAvailability(root) {
    const available = variantAvailable(root);
    if (root.dataset.signedIn === 'true') {
      // A signed-in customer sees the widget for EVERY selected variant; availability only picks
      // the look (data-stock), never visibility. The load then decides what it can show.
      root.dataset.stock = available ? 'in' : 'out';
      root.hidden = false;
      loadHeartFor(root); // every (re)entry starts from loading and re-fetches: the fallback is per variant
      return;
    }
    root.hidden = available; // signed-out: unchanged, hidden while the variant is available
    if (available) return;
    if (root.classList.contains(STATE_CLASSES.success)) {
      // a different sold-out variant: offer the form again
      root.classList.remove(STATE_CLASSES.success);
      root.querySelector('.kns-notify__form').hidden = false;
      root.querySelector('.kns-notify__lead').hidden = false;
      root.querySelector('[data-kns-notify-message]').hidden = true;
    }
  }

  // ---- heart (signed-in) -------------------------------------------------

  // The ONE painter of the signed-in widget. `view` is exactly one of 'loading' | 'off' | 'on' |
  // 'form' | 'hidden'; `message` (always an error in this path) may accompany any view. Nothing
  // else in the signed-in path may touch what this sets: the root's visibility, heart block/lead
  // visibility, pressed/label/aria-label, the wishlist link, the sold-out title, form and
  // form-lead visibility, and the message's text, visibility, error and success classes. Entering
  // any view clears a previous message unless a new one is passed, so no failure text outlives
  // the variant it belonged to.
  function renderSignedIn(root, view, message) {
    if (root.dataset.signedIn !== 'true' || !HEART_VIEWS.includes(view)) return;
    const heartBlock = root.querySelector('[data-kns-notify-heart-block]');
    const heart = root.querySelector('[data-kns-notify-heart]');
    const heartLead = root.querySelector('[data-kns-notify-heart-lead]');
    const label = root.querySelector('[data-kns-notify-heart-label]');
    const svg = heart?.querySelector('.kns-notify__heart-svg');
    const form = root.querySelector('.kns-notify__form');
    const formLead = root.querySelector('[data-kns-notify-form-lead]');
    const messageEl = root.querySelector('[data-kns-notify-message]');
    const link = root.querySelector('[data-kns-notify-wishlist-link]');
    const title = root.querySelector('[data-kns-notify-title]');
    if (!heartBlock || !heart || !heartLead || !form || !formLead || !messageEl || !link || !title) return;

    // 'hidden': nothing to offer this variant (an in-stock knife with no reachable wishlist).
    root.hidden = view === 'hidden';
    const showHeart = view !== 'form' && view !== 'hidden'; // 'form' is the sold-out fallback: heart goes, form comes back
    heartBlock.hidden = !showHeart;
    heartLead.hidden = !showHeart;
    form.hidden = showHeart;
    formLead.hidden = showHeart;
    // The sold-out title belongs to the 'out' look only; the link is the 'on' view's confirmation.
    title.hidden = root.dataset.stock !== 'out' || !showHeart;
    link.hidden = view !== 'on';

    const on = view === 'on';
    const copy = HEART_COPY[root.dataset.stock === 'in' ? 'in' : 'out'][on ? 'on' : 'off'];
    heart.setAttribute('aria-pressed', String(on));
    heart.setAttribute('aria-label', copy.ariaLabel);
    heart.setAttribute('aria-busy', view === 'loading' ? 'true' : 'false');
    heart.classList.toggle('kns-notify__heart-btn--on', on);
    if (label) label.textContent = copy.label;
    heartLead.textContent = copy.lead;
    if (svg) svg.classList.toggle('kns-notify__heart-svg--filled', on);
    heart.dataset.on = String(on);

    if (message) {
      messageEl.textContent = message;
      messageEl.hidden = false;
      root.classList.add(STATE_CLASSES.error);
    } else {
      messageEl.textContent = '';
      messageEl.hidden = true;
      root.classList.remove(STATE_CLASSES.error);
    }
    root.classList.remove(STATE_CLASSES.success); // a previous variant's submit-success look must not leak
  }

  async function loadHeartFor(root) {
    const variantId = selectedVariant(root);
    if (!Number.isInteger(variantId)) return;
    const seq = nextSeq(root);
    renderSignedIn(root, 'loading'); // OFF and busy; clears any stale message at once
    try {
      const response = await fetch(`/apps/tripcoins/notify/heart?variant_id=${variantId}`);
      if (!response.ok) throw { status: response.status };
      const data = await response.json();
      if (isStale(root, seq)) return; // a newer request owns the widget now
      renderSignedIn(root, data?.on ? 'on' : 'off');
    } catch (error) {
      if (isStale(root, seq)) return;
      const status = error?.status;
      if (root.dataset.stock === 'in') {
        // B7: an in-stock knife has no form to fall back to and no error to show for a load the
        // customer never started — any load failure hides the widget silently.
        renderSignedIn(root, 'hidden');
        return;
      }
      if (status === 401) renderSignedIn(root, 'form'); // not signed in: silent fallback
      else if (status === 409) renderSignedIn(root, 'form', MESSAGES.heartNoEmail);
      else if (status === 429) renderSignedIn(root, 'form', MESSAGES.heartThrottled);
      else renderSignedIn(root, 'form', MESSAGES.heartLoadFailed); // never a dead heart
    }
  }

  function bindHeart(root) {
    const heart = root.querySelector('[data-kns-notify-heart]');
    if (!heart || heart.dataset.knsHeartBound === 'true') return;
    heart.dataset.knsHeartBound = 'true';

    // Taps are ignored while a request is outstanding. The button is NOT disabled (that would
    // drop keyboard focus to <body>); the busy look is aria-busy instead.
    let tapInFlight = false;
    heart.addEventListener('click', async () => {
      if (tapInFlight || heart.getAttribute('aria-busy') === 'true') return;
      const variantId = selectedVariant(root);
      const productId = Number.parseInt(root.dataset.productId, 10);
      if (!Number.isInteger(variantId) || !Number.isInteger(productId)) return;

      const before = heart.dataset.on !== 'false'; // current painted state
      const seq = nextSeq(root); // a variant change while this POST flies retires its answer
      tapInFlight = true;
      heart.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch('/apps/tripcoins/notify/heart', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ product_id: productId, variant_id: variantId, on: !before }),
        });
        if (!response.ok) throw { status: response.status };
        const data = await response.json();
        if (isStale(root, seq)) return;
        renderSignedIn(root, data?.on ? 'on' : 'off'); // the server's stored state, never optimistic
      } catch (error) {
        if (isStale(root, seq)) return;
        const status = error?.status;
        // 401/409 hide the customer's wishlist either way; on an in-stock knife there is no
        // email-form fallback, so the whole widget goes. Other failures keep the pre-tap state.
        if (status === 401 || status === 409) {
          if (root.dataset.stock === 'in') renderSignedIn(root, 'hidden');
          else if (status === 401) renderSignedIn(root, 'form');
          else renderSignedIn(root, 'form', MESSAGES.heartNoEmail);
        } else {
          renderSignedIn(root, before ? 'on' : 'off', status === 429 ? MESSAGES.heartThrottled : MESSAGES.heartNetwork);
        }
      } finally {
        tapInFlight = false;
      }
    });
  }

  // ---- end heart ---------------------------------------------------------

  // ---- wishlist page (parcel WL-UI-2) -------------------------------------
  // Marked-up by snippets/kns-wishlist.liquid; the browser fixture in
  // heart_browser_check.py hand-writes that snippet's output with the same
  // classes and data attributes.

  function wishlistStatus(list, text) {
    const status = list.querySelector('[data-kns-wishlist-status]');
    if (!status) return;
    status.textContent = text;
    status.hidden = !text;
  }

  function bindWishlist(root) {
    if (root.dataset.knsWishlistBound === 'true') return;
    root.dataset.knsWishlistBound = 'true';
    const heading = root.querySelector('[data-kns-wishlist-heading]');
    const empty = root.querySelector('[data-kns-wishlist-empty]');

    root.addEventListener('click', async (event) => {
      const button = event.target.closest?.('[data-kns-wishlist-remove]');
      if (!button || !root.contains(button) || button.getAttribute('aria-busy') === 'true') return;
      const productId = Number.parseInt(button.dataset.productId, 10);
      const variantId = Number.parseInt(button.dataset.variantId, 10);
      if (!Number.isInteger(productId) || !Number.isInteger(variantId)) return;

      // Busy is aria-busy, not disabled (disabling would move keyboard focus); taps while busy
      // are ignored here and by the guard above.
      button.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch('/apps/tripcoins/notify/heart', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ product_id: productId, variant_id: variantId, on: false }),
        });
        if (response.status === 401) {
          window.location.reload(); // the signed-out view will render
          return;
        }
        const data = await response.json().catch(() => null);
        if (!response.ok || !data || data.on !== false) {
          wishlistStatus(root, "That didn't work. Please try again.");
          return;
        }
        const item = button.closest('[data-kns-wishlist-item]');
        const next = item?.nextElementSibling?.querySelector('[data-kns-wishlist-remove]');
        item?.remove();
        wishlistStatus(root, ''); // a later success clears an earlier error
        if (next) next.focus();
        else heading?.focus();
        const anyLeft = root.querySelector('[data-kns-wishlist-item]');
        if (empty && !anyLeft) empty.hidden = false;
      } catch {
        wishlistStatus(root, "That didn't work. Please try again.");
      } finally {
        button.removeAttribute('aria-busy');
      }
    });
  }

  // ---- end wishlist page --------------------------------------------------

  function initialise(root) {
    if (root.dataset.knsNotifyReady === 'true') return root;
    root.dataset.knsNotifyReady = 'true';

    // The form's own lead (signed-out markup has exactly one .kns-notify__lead, so this only
    // matters for a signed-in customer, whose FIRST lead belongs to the heart).
    const formLead = root.querySelector('[data-kns-notify-form-lead]') ?? root.querySelector('.kns-notify__lead');
    const form = root.querySelector('.kns-notify__form');
    const email = root.querySelector('[type="email"]');
    const honeypot = root.querySelector('[name="hp"]');
    const submit = root.querySelector('.kns-notify__submit');

    // Bind to a TRUE common ancestor: the buy-buttons block and the variant-picker block are
    // sibling subtrees, so 'on:variant:change' never passes through root.parentElement (review F1).
    const host = root.parentElement?.closest('.product, shopify-section, section') ?? root.parentElement;
    host?.addEventListener('on:variant:change', () => syncAvailability(root));
    root.parentElement?.addEventListener('change', () => syncAvailability(root));
    if (root.dataset.signedIn === 'true') bindHeart(root);
    syncAvailability(root);

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const address = email.value.trim();
      const variantId = selectedVariant(root);
      const productId = Number.parseInt(root.dataset.productId, 10);

      if (!emailPattern.test(address) || !Number.isInteger(variantId) || !Number.isInteger(productId)) {
        showMessage(root, MESSAGES.invalid, true);
        email.focus();
        return;
      }

      Object.values(STATE_CLASSES).forEach((className) => root.classList.remove(className));
      root.classList.add(STATE_CLASSES.submitting);
      submit.disabled = true;
      email.disabled = true;

      try {
        const response = await fetch(root.dataset.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: address, variant_id: variantId, product_id: productId, hp: honeypot.value }),
        });

        if (response.ok) {
          root.classList.add(STATE_CLASSES.success);
          showMessage(root, MESSAGES.success, false); // role=status announces it
          form.hidden = true;
          formLead.hidden = true;
        } else if (response.status === 400) {
          showMessage(root, MESSAGES.invalid, true);
          email.focus();
        } else if (response.status === 429) {
          showMessage(root, MESSAGES.throttled, true);
        } else {
          showMessage(root, MESSAGES.network, true);
        }
      } catch {
        showMessage(root, MESSAGES.network, true);
      } finally {
        root.classList.remove(STATE_CLASSES.submitting);
        submit.disabled = false;
        email.disabled = false;
      }
    });

    return root;
  }

  function initAll() {
    document.querySelectorAll('[data-kns-notify-root]').forEach(initialise);
    document.querySelectorAll('[data-kns-wishlist-root]').forEach(bindWishlist);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  window.KnsNotify = {
    initialise,
    syncAvailability,
  };
})();
